<template>
    <div>
        <md-field
            v-if="
                customFieldDefinition.type.toLowerCase() == 'text' ||
                    customFieldDefinition.type.toLowerCase() == 'multilinetext'
            "
            :class="{ 'md-invalid': showError }"
        >
            <label>{{ showLabel ? fieldLabel : null }}</label>
            <md-input
                v-if="customFieldDefinition.type.toLowerCase() == 'text'"
                v-model="customFieldValue"
                :placeholder="placeholder"
                :disabled="disabled"
                @blur="handleBlur"
            />
            <md-textarea
                v-if="customFieldDefinition.type.toLowerCase() == 'multilinetext'"
                v-model="customFieldValue"
                :disabled="disabled"
                @blur="handleBlur"
            />
            <span v-if="showError" class="md-error">{{ customFieldDefinition.label }} is required</span>
        </md-field>

        <md-field v-if="customFieldDefinition.type.toLowerCase() == 'date'" :class="{ 'md-invalid': showError }">
            <md-datepicker
                v-model="customFieldValue"
                md-immediately
                :md-debounce="10"
                :disabled="disabled"
                @md-closed="handleBlur"
            >
                <label>{{ showLabel ? fieldLabel : null }}</label>
            </md-datepicker>
            <span v-if="showError" class="md-error">{{ customFieldDefinition.label }} is required</span>
        </md-field>

        <md-checkbox
            v-if="customFieldDefinition.type.toLowerCase() == 'checkbox'"
            v-model="customFieldValue"
            :disabled="disabled"
        >
            {{ showLabel ? fieldLabel : '' }}
        </md-checkbox>
        <md-field v-if="customFieldDefinition.type.toLowerCase() === 'dropdown'" :class="{ 'md-invalid': showError }">
            <label>{{ showLabel ? fieldLabel : null }}</label>
            <vue-select
                :reduce="(cf) => cf.value"
                :label="showDropdownLabelAs"
                :options="customFieldDefinition.options"
                :placeholder="customFieldDefinition.label"
                v-model="customFieldValue"
                :searchable="false"
                class="custom-mapping-select"
                :disabled="disabled"
                @close="handleBlur"
            ></vue-select>
            <span v-if="showError" class="md-error">{{ customFieldDefinition.label }} is required</span>
        </md-field>

        <md-field
            v-if="customFieldDefinition.type.toLowerCase() === 'radiobutton'"
            :class="{ 'md-invalid': showError }"
        >
            <label class="radio-group-label">{{ showLabel ? fieldLabel : null }}</label>
            <div class="radio-button-group">
                <md-radio
                    v-for="(option, index) in customFieldDefinition.options"
                    :key="index"
                    v-model="customFieldValue"
                    :value="option.value"
                    :disabled="disabled"
                >
                    {{ option.value }}
                </md-radio>
                <md-button
                    v-if="
                        !customFieldDefinition.isRequired &&
                            customFieldValue !== null &&
                            customFieldValue !== undefined &&
                            customFieldValue !== ''
                    "
                    class="md-just-icon md-simple radio-clear-button"
                    title="Clear selection"
                    :disabled="disabled"
                    @click.native="customFieldValue = null"
                >
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <span v-if="showError" class="md-error">{{ customFieldDefinition.label }} is required</span>
        </md-field>
    </div>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { DATE_TYPES } from '@/utils/constants';
import moment from 'moment';

export default {
    name: 'CustomFieldInputs',
    mixins: [GeneralMixin],
    props: {
        customFieldDefinition: {
            type: Object,
            default: () => {}
        },
        // eslint-disable-next-line vue/require-default-prop
        initialValue: [String, Number, Date, Boolean],
        showLabel: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showDropdownLabelAs: { type: String, default: 'value' },
        touched: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        fieldLabel() {
            if (!this.customFieldDefinition.label) 
                return null;
            return this.customFieldDefinition.isRequired
                ? `${this.customFieldDefinition.label} *`
                : this.customFieldDefinition.label;
        },
        isInvalid() {
            if (!this.customFieldDefinition.isRequired) 
                return false;
            const val = this.customFieldValue;
            return val === null || val === undefined || val === '';
        },
        showError() {
            return (this.isTouched || this.touched) && this.isInvalid;
        }
    },
    watch: {
        customFieldValue(newValue, oldValue) {
            let val = newValue;
            if (this.customFieldDefinition.type.toLowerCase() === 'date' && val) {
                val = !moment(val).isValid() ? null : moment(val).format(DATE_TYPES.standardDate);
            }

            if (this.customFieldDefinition.type.toLowerCase() === 'checkbox') {
                val = !!val; // force boolean
            }

            // handle undefined and null
            const newVal = newValue || '';
            const oldVal = oldValue || '';

            if (oldVal !== newVal) {
                this.$emit('changed', {
                    name: this.customFieldDefinition.name,
                    value: val,
                    type: this.customFieldDefinition.type,
                    isValid: !this.customFieldDefinition.isRequired || (val !== null && val !== undefined && val !== '')
                });
            }
        },
        customFieldDefinition: {
            immediate: true,
            handler() {
                // update because consumer may change definitions
                this.updateFields();
            }
        },
        initialValue() {
            this.updateFields();
        }
    },
    methods: {
        updateFields() {
            if (this.initialValue === false && this.customFieldDefinition?.type.toLowerCase() !== 'checkbox') {
                this.customFieldValue = null;
            } else if (!!this.initialValue && this.customFieldDefinition?.type.toLowerCase() === 'date') {
                this.customFieldValue = moment(this.initialValue).format(DATE_TYPES.internationalDate);
            } else {
                this.customFieldValue = this.initialValue;
            }
        },
        handleBlur() {
            this.isTouched = true;
        }
    },
    mounted() {
        this.updateFields();
    },
    data() {
        return {
            customFieldValue: null,
            initialValueHasLoaded: false,
            isTouched: false
        };
    }
};
</script>

<style lang="scss" scoped>
.custom-mapping-select {
    width: 100%;
    ::v-deep .vs__clear {
        display: none;
    }
    ::v-deep .vs__search,
    .vs__search:focus {
        font-size: 14px;
        padding: 0;
    }

    ::v-deep .vs__selected-options {
        padding-left: 5px;
        display: inline-flex;
        flex-grow: unset;
        flex-wrap: unset;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    ::v-deep ::placeholder {
        /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: #aaa;
        opacity: 1; /* Firefox */
        padding: 0;
    }

    ::v-deep :-ms-input-placeholder {
        /* Internet Explorer 10-11 */
        color: #aaa;
        padding: 0;
    }

    ::v-deep ::-ms-input-placeholder {
        /* Microsoft Edge */
        color: #aaa;
        padding: 0;
    }

    ::v-deep .vs__dropdown-menu {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

::v-deep .md-field.md-theme-default label:not(.md-radio-label) {
    top: -0.8rem !important;
    font-size: 0.6875rem !important;
}

.md-error {
    bottom: -10px !important;
}

.radio-button-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    gap: 0.5rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;

    ::v-deep .md-radio {
        display: inline-flex;
        align-items: center;
        margin: 0;

        .md-radio-label {
            line-height: 1;
            padding-left: 0.25rem;
        }
    }
}

.radio-clear-button {
    margin: 0 !important;
    ::v-deep .md-icon {
        font-size: 18px !important;
    }
}

.radio-group-label {
    position: absolute !important;
    top: -0.8rem !important;
    font-size: 0.6875rem !important;
}
</style>
